"use client";

import { useFeatureFlag } from "@posthog/next";
import { useEffect, useState } from "react";

export type HydrationSafeFeatureFlagState = {
  /** False on SSR and the client's first paint; true after mount. */
  ready: boolean;
  /** `ready &&` PostHog reports the flag enabled. */
  enabled: boolean;
};

/**
 * Hydration-safe feature-flag check for UI that must match SSR markup.
 *
 * `@posthog/next` initializes posthog-js during render on the client and can
 * restore persisted flags before hydration. SSR typically sees `undefined`, so
 * reading `?.enabled === true` on the first client paint can add/remove DOM
 * (e.g. nav Links) and trigger React #418.
 *
 * Prefer {@link useHydrationSafeFeatureFlagState} when a false `enabled` must
 * not trigger side effects (network) until the flag has actually resolved.
 */
export function useHydrationSafeFeatureFlagState(
  flagKey: string
): HydrationSafeFeatureFlagState {
  const rawEnabled = useFeatureFlag(flagKey)?.enabled === true;
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setReady(true);
  }, []);

  return { ready, enabled: ready && rawEnabled };
}

/**
 * Returns `false` on the server and on the client's first paint; after mount
 * returns whether the flag is enabled.
 */
export function useHydrationSafeFeatureFlagEnabled(flagKey: string): boolean {
  return useHydrationSafeFeatureFlagState(flagKey).enabled;
}
